/**
 * materin-ui 库主入口
 *
 * 使用：
 *   import { createApp } from 'vue'
 *   import MaterinUI from '@materin-tech/materin-ui'
 *   import '@materin-tech/materin-ui/styles'   // 组件样式
 *   import '@materin-tech/materin-ui/tokens'   // Materin UI 令牌表
 *   createApp(App).use(MaterinUI).mount('#app')
 *
 * 命名契约：materin-<scope>-<component>[__part][--variant]
 * scope: ui (共享件) | site | docs | office | view | ctx
 *
 * 本文件由 tools/gen-index.py 生成（新增组件后重跑），不要手改 import 列表。
 */

import type { App, Component } from 'vue'

declare const __MI_VERSION__: string

import MiAffix from '../components/affix'
import MiAlert from '../components/alert'
import MiAnchor from '../components/anchor'
import MiAside from '../components/aside'
import MiAutocomplete from '../components/autocomplete'
import MiAvatar from '../components/avatar'
import MiBacktop from '../components/backtop'
import MiBadge from '../components/badge'
import MiBreadcrumb from '../components/breadcrumb'
import MiButton from '../components/button'
import MiButtonGroup from '../components/button-group'
import MiCalendar from '../components/calendar'
import MiCard from '../components/card'
import MiCarousel from '../components/carousel'
import MiCascader from '../components/cascader'
import MiCheckbox from '../components/checkbox'
import MiCheckboxGroup from '../components/checkbox-group'
import MiCol from '../components/col'
import MiCollapse from '../components/collapse'
import MiCollapseItem from '../components/collapse-item'
import MiColorPicker from '../components/color-picker'
import MiConfigProvider from '../components/config-provider'
import MiContainer from '../components/container'
import MiCopy from '../components/copy'
import MiDatePicker from '../components/date-picker'
import MiDescriptions from '../components/descriptions'
import MiDialog from '../components/dialog'
import MiDivider from '../components/divider'
import MiDrawer from '../components/drawer'
import MiDropdown from '../components/dropdown'
import MiEmpty from '../components/empty'
import MiFooter from '../components/footer'
import MiForm from '../components/form'
import MiFormItem from '../components/form-item'
import MiGlassPanel from '../components/glass-panel'
import MiHeader from '../components/header'
import MiIcon from '../components/icon'
import MiImage from '../components/image'
import MiInfiniteScroll from '../components/infinite-scroll'
import MiInput from '../components/input'
import MiInputNumber from '../components/input-number'
import MiLink from '../components/link'
import MiLoading from '../components/loading'
import MiMain from '../components/main'
import MiMention from '../components/mention'
import MiMenu from '../components/menu'
import MiMessage from '../components/message'
import MiMessageBox from '../components/message-box'
import MiMessageItem from '../components/message-item'
import MiMethodTag from '../components/method-tag'
import MiNotification from '../components/notification'
import MiNotificationItem from '../components/notification-item'
import MiOption from '../components/option'
import MiOptionGroup from '../components/option-group'
import MiPageHeader from '../components/page-header'
import MiPageShell from '../components/page-shell'
import MiPagination from '../components/pagination'
import MiPopconfirm from '../components/popconfirm'
import MiPopover from '../components/popover'
import MiProgress from '../components/progress'
import MiQRCode from '../components/qrcode'
import MiRadio from '../components/radio'
import MiRadioGroup from '../components/radio-group'
import MiRate from '../components/rate'
import MiResult from '../components/result'
import MiRow from '../components/row'
import MiScrollbar from '../components/scrollbar'
import MiSegmented from '../components/segmented'
import MiSelect from '../components/select'
import MiSkeleton from '../components/skeleton'
import MiSkeletonItem from '../components/skeleton-item'
import MiSlider from '../components/slider'
import MiSpace from '../components/space'
import MiSplitter from '../components/splitter'
import MiStatistic from '../components/statistic'
import MiStatusDot from '../components/status-dot'
import MiSteps from '../components/steps'
import MiSwitch from '../components/switch'
import MiTable from '../components/table'
import MiTableColumn from '../components/table-column'
import MiTabs from '../components/tabs'
import MiTag from '../components/tag'
import MiText from '../components/text'
import MiTimePicker from '../components/time-picker'
import MiTimeSelect from '../components/time-select'
import MiTimeline from '../components/timeline'
import MiTimelineItem from '../components/timeline-item'
import MiTitle from '../components/title'
import MiTooltip from '../components/tooltip'
import MiTour from '../components/tour'
import MiTransfer from '../components/transfer'
import MiTree from '../components/tree'
import MiTreeSelect from '../components/tree-select'
import MiUpload from '../components/upload'
import MiWatermark from '../components/watermark'

const components = [MiAffix, MiAlert, MiAnchor, MiAside, MiAutocomplete, MiAvatar, MiBacktop, MiBadge, MiBreadcrumb, MiButton, MiButtonGroup, MiCalendar, MiCard, MiCarousel, MiCascader, MiCheckbox, MiCheckboxGroup, MiCol, MiCollapse, MiCollapseItem, MiColorPicker, MiConfigProvider, MiContainer, MiCopy, MiDatePicker, MiDescriptions, MiDialog, MiDivider, MiDrawer, MiDropdown, MiEmpty, MiFooter, MiForm, MiFormItem, MiGlassPanel, MiHeader, MiIcon, MiImage, MiInfiniteScroll, MiInput, MiInputNumber, MiLink, MiLoading, MiMain, MiMention, MiMenu, MiMessage, MiMessageBox, MiMessageItem, MiMethodTag, MiNotification, MiNotificationItem, MiOption, MiOptionGroup, MiPageHeader, MiPageShell, MiPagination, MiPopconfirm, MiPopover, MiProgress, MiQRCode, MiRadio, MiRadioGroup, MiRate, MiResult, MiRow, MiScrollbar, MiSegmented, MiSelect, MiSkeleton, MiSkeletonItem, MiSlider, MiSpace, MiSplitter, MiStatistic, MiStatusDot, MiSteps, MiSwitch, MiTable, MiTableColumn, MiTabs, MiTag, MiText, MiTimePicker, MiTimeSelect, MiTimeline, MiTimelineItem, MiTitle, MiTooltip, MiTour, MiTransfer, MiTree, MiTreeSelect, MiUpload, MiWatermark]

const install = (app: App): void => {
  components.forEach((component) => {
    // 命令式服务（如 MiMessage / MiNotification）是函数，不是组件，不注册
    if (typeof component === 'function') return
    const named = component as unknown as { name?: string; __name?: string }
    app.component(named.name || named.__name || 'MiComponent', component as unknown as Component)
  })
}

export {MiAffix, MiAlert, MiAnchor, MiAside, MiAutocomplete, MiAvatar, MiBacktop, MiBadge, MiBreadcrumb, MiButton, MiButtonGroup, MiCalendar, MiCard, MiCarousel, MiCascader, MiCheckbox, MiCheckboxGroup, MiCol, MiCollapse, MiCollapseItem, MiColorPicker, MiConfigProvider, MiContainer, MiCopy, MiDatePicker, MiDescriptions, MiDialog, MiDivider, MiDrawer, MiDropdown, MiEmpty, MiFooter, MiForm, MiFormItem, MiGlassPanel, MiHeader, MiIcon, MiImage, MiInfiniteScroll, MiInput, MiInputNumber, MiLink, MiLoading, MiMain, MiMention, MiMenu, MiMessage, MiMessageBox, MiMessageItem, MiMethodTag, MiNotification, MiNotificationItem, MiOption, MiOptionGroup, MiPageHeader, MiPageShell, MiPagination, MiPopconfirm, MiPopover, MiProgress, MiQRCode, MiRadio, MiRadioGroup, MiRate, MiResult, MiRow, MiScrollbar, MiSegmented, MiSelect, MiSkeleton, MiSkeletonItem, MiSlider, MiSpace, MiSplitter, MiStatistic, MiStatusDot, MiSteps, MiSwitch, MiTable, MiTableColumn, MiTabs, MiTag, MiText, MiTimePicker, MiTimeSelect, MiTimeline, MiTimelineItem, MiTitle, MiTooltip, MiTour, MiTransfer, MiTree, MiTreeSelect, MiUpload, MiWatermark}

export default {
  install,
  version: typeof __MI_VERSION__ === 'string' ? __MI_VERSION__ : '0.0.0-dev'
}

export type { AffixProps } from '../components/affix/types'
export type { AlertProps, AlertType } from '../components/alert/types'
export type { AnchorProps, AnchorLink } from '../components/anchor/types'
export type { AsideProps } from '../components/aside/types'
export type { AutocompleteProps } from '../components/autocomplete/types'
export type { AvatarProps, AvatarSize } from '../components/avatar/types'
export type { BacktopProps } from '../components/backtop/types'
export type { BadgeProps, BadgeType } from '../components/badge/types'
export type { BreadcrumbProps, Crumb } from '../components/breadcrumb/types'
export type { ButtonProps, ButtonEmits } from '../components/button/types'
export type { ButtonGroupProps } from '../components/button-group/types'
export type { CalendarProps } from '../components/calendar/types'
export type { CardProps } from '../components/card/types'
export type { CarouselProps } from '../components/carousel/types'
export type { CascaderProps, CascaderOption } from '../components/cascader/types'
export type { CheckboxProps } from '../components/checkbox/types'
export type { CheckboxGroupProps } from '../components/checkbox-group/types'
export type { ColProps, ColSize } from '../components/col/types'
export type { CollapseProps, CollapseItemProps, CollapseContext } from '../components/collapse/types'
export type { ColorPickerProps } from '../components/color-picker/types'
export type { ConfigProviderProps } from '../components/config-provider/types'
export type { ContainerProps } from '../components/container/types'
export type { CopyProps } from '../components/copy/types'
export type { DatePickerProps } from '../components/date-picker/types'
export type { DescriptionsProps, DescriptionsItem } from '../components/descriptions/types'
export type { DialogProps } from '../components/dialog/types'
export type { DividerProps } from '../components/divider/types'
export type { DrawerProps } from '../components/drawer/types'
export type { DropdownProps, DropdownOption } from '../components/dropdown/types'
export type { EmptyProps } from '../components/empty/types'
export type { FooterProps } from '../components/footer/types'
export type { FormProps, FormRule } from '../components/form/types'
export type { FormItemProps } from '../components/form-item/types'
export type { GlassPanelProps } from '../components/glass-panel/types'
export type { HeaderProps } from '../components/header/types'
export type { IconProps } from '../components/icon/types'
export type { ImageProps } from '../components/image/types'
export type { InfiniteScrollProps } from '../components/infinite-scroll/types'
export type { InputProps, InputEmits } from '../components/input/types'
export type { InputNumberProps } from '../components/input-number/types'
export type { LinkProps, LinkType } from '../components/link/types'
export type { LoadingProps } from '../components/loading/types'
export type { MainProps } from '../components/main/types'
export type { MentionProps } from '../components/mention/types'
export type { MenuProps } from '../components/menu/types'
export type { MessageHandle, MessageOptions, MessageRecord, MessageType } from '../components/message/types'
export type { MethodTagProps } from '../components/method-tag/types'
export type { OptionProps } from '../components/option/types'
export type { OptionGroupProps } from '../components/option-group/types'
export type { PageHeaderProps } from '../components/page-header/types'
export type { PageShellProps } from '../components/page-shell/types'
export type { PaginationProps } from '../components/pagination/types'
export type { PopconfirmProps } from '../components/popconfirm/types'
export type { PopoverProps } from '../components/popover/types'
export type { ProgressProps } from '../components/progress/types'
export type { QRCodeProps } from '../components/qrcode/types'
export type { RadioProps } from '../components/radio/types'
export type { RadioGroupProps } from '../components/radio-group/types'
export type { RateProps } from '../components/rate/types'
export type { ResultProps } from '../components/result/types'
export type { RowProps } from '../components/row/types'
export type { ScrollbarProps } from '../components/scrollbar/types'
export type { SegmentedProps, SegmentedOption } from '../components/segmented/types'
export type { SelectProps } from '../components/select/types'
export type { SkeletonProps } from '../components/skeleton/types'
export type { SkeletonItemProps, SkeletonVariant } from '../components/skeleton-item/types'
export type { SliderProps } from '../components/slider/types'
export type { SpaceProps } from '../components/space/types'
export type { SplitterProps } from '../components/splitter/types'
export type { StatisticProps } from '../components/statistic/types'
export type { StatusDotProps } from '../components/status-dot/types'
export type { StepsProps, StepDef } from '../components/steps/types'
export type { SwitchProps } from '../components/switch/types'
export type { TableProps, TableColumn } from '../components/table/types'
export type { TabsProps, TabPane } from '../components/tabs/types'
export type { TagProps, TagEmits } from '../components/tag/types'
export type { TextProps, TextType, TextSize } from '../components/text/types'
export type { TimePickerProps } from '../components/time-picker/types'
export type { TimeSelectProps } from '../components/time-select/types'
export type { TimelineProps, TimelineItemProps } from '../components/timeline/types'
export type { TitleProps, TitleType } from '../components/title/types'
export type { TooltipProps } from '../components/tooltip/types'
export type { TourProps, TourStep } from '../components/tour/types'
export type { TransferProps, TransferItem } from '../components/transfer/types'
export type { TreeProps, TreeNode } from '../components/tree/types'
export type { TreeSelectProps } from '../components/tree-select/types'
export type { UploadProps, UploadItem } from '../components/upload/types'
export type { WatermarkProps } from '../components/watermark/types'
